/*
 *
 * ExportPopUp
 *
 * @version 1.0.0
 * @author Magarelli-Fabrizio
 * @since 07-Nov-2024
 */

import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import { useFormik } from 'formik'
import moment from 'moment'
import * as Yup from 'yup'
import DatePicker from '@nexidigital/datepicker'
import MerchantModal from '@nexidigital/merchantmodal'
import MerchantTimePicker from '@nexidigital/merchanttimepicker'
import PortalRadioBox from '@nexidigital/portalradiobox'
import Toggle from 'Hoc/ToggleComponent'
import {
  outOfRangeCalendar,
  minDate,
  maxDate,
  joinDateAndTime,
  momentLongDateFormat,
  timeErrorDetect
} from 'Utility/dateMethods'
import { getLanguageCalendar } from 'Utility/globalMethods'
import { is12HourFormat } from 'Utility/globalMethods'
import formattedMessage, { captions } from './languageModule'
import './ExportPopUp.scss'

const ExportPopUp = ({
  actions,
  isLoading,
  reserchFromDate,
  reserchToDate,
  isOperation,
  isPbl,
  reserchFromTime,
  reserchToTime
}) => {
  const intl = useIntl()
  const [datesOptions, setDatesOptions] = useState([])
  const format = ['csv', 'xlsx']
  const dates = ['TODAY', 'CURRENT_MONTH', 'LAST_SEVEN_DAYS', 'LAST_MONTH', 'CUSTOM']
  const formatDate = momentLongDateFormat()
  
  const formik = useFormik({
    initialValues: {
      date: {
        from: moment().startOf('day').utc().toISOString(), //TODAY- START
        to: moment().utc().toISOString() //TODAY- END
      },
      dateType: reserchFromDate || reserchToDate ? 'CUSTOM' : 'TODAY',
      format: 'xlsx',
      customDate: {
        from: moment(reserchFromDate, formatDate) || '',
        to: moment(reserchToDate, formatDate) || ''
      },
      customTime: {
        from: reserchFromTime ? reserchFromTime : moment().startOf('day'),
        to: reserchToTime
          ? reserchToTime
          : reserchToDate && moment(reserchToDate).format('L') === moment().format('L')
            ? moment()
            : moment().endOf('day')
      }
    },
    validationSchema: Yup.object({
      customDate: Yup.object({
        from: Yup.string()
          .nullable()
          .test(
            'is-less',
            intl.formatMessage({
              id: 't.app.orders.export.popup.timeError',
              defaultMessage: 'The time range is not valid'
            }),
            function (value) {
              let { to } = this.parent
        
              if (!moment(value, formatDate).isValid() || !moment(to, formatDate).isValid()) {
                return true
              }
              return (
                moment(value, formatDate).isSameOrBefore(moment(to, formatDate)) &&
                moment(value, formatDate).isSameOrAfter(moment(to, formatDate).subtract(30, 'days'), 'day') &&
                moment(value, formatDate).isSameOrAfter(moment().subtract(13, 'months'), 'day')
              )
            }
          )
          .test(
            'is-not-future',
            intl.formatMessage({
              id: 't.app.pblManagement.research.validator.validDate',
              defaultMessage: 'Enter a valid date'
            }),
            (value) => {
              if (!value || !moment(value, formatDate).isValid()) return true
              return moment(value, formatDate).isSameOrBefore(moment(), 'day')
            }
          ),
        to: Yup.string()
          .nullable()
          .test(
            'is-greater',
            intl.formatMessage({
              id: 't.app.orders.export.popup.timeError',
              defaultMessage: 'The time range is not valid'
            }),
            function (value) {
              let { from } = this.parent

              if (!moment(value, formatDate).isValid() || !moment(from, formatDate).isValid()) {
                return true
              }
              return (
                moment(value, formatDate).isSameOrAfter(moment(from, formatDate)) &&
                moment(value, formatDate).isSameOrAfter(moment(from, formatDate).subtract(30, 'days'), 'day') &&
                moment(value, formatDate).isSameOrAfter(moment().subtract(13, 'months'), 'day')
              )
            }
          )
          .test(
            'is-not-future',
            intl.formatMessage({
              id: 't.app.pblManagement.research.validator.validDate',
              defaultMessage: 'Enter a valid date'
            }),
            (value) => {
              if (!value || !moment(value, formatDate).isValid()) return true
              return moment(value, formatDate).isSameOrBefore(moment(), 'day')
            }
          )
      }),
      customTime: Yup.object({
        from: Yup.mixed()
          .nullable()
          .test(
            'time-from-before-to',
            intl.formatMessage({
              id: 't.app.orders.export.popup.timeError',
              defaultMessage: 'The time range is not valid'
            }),
            function (value) {
              const { to } = this.parent
              if (!value || !to || !moment(value).isValid() || !moment(to).isValid()) {
                return true
              }
              return moment(value).isBefore(moment(to))
            }
          ),
        to: Yup.mixed()
          .nullable()
          .test(
            'time-to-after-from',
            intl.formatMessage({
              id: 't.app.orders.export.popup.timeError',
              defaultMessage: 'The time range is not valid'
            }),
            function (value) {
              const { from } = this.parent
              if (!value || !from || !moment(value).isValid() || !moment(from).isValid()) {
                return true
              }
              return moment(value).isAfter(moment(from))
            }
          )
      })
    })
  })
  const [fromDate, setFromDate] = useState(formik?.values?.customDate?.from ? formik?.values?.customDate?.from : '')

  const calcDateRange = (key, type) => {
    let startDate = ''
    let endDate = ''
    let result = ''

    switch (type) {
    case 'TODAY':
    case undefined: {
      if (key === 'value') {
        startDate = moment().startOf('day').utc().toISOString()
        endDate = moment().utc().toISOString()
      }
      if (key === 'label') {
        result = moment().format('L')
      }
      break
    }

    case 'CURRENT_MONTH': {
      const firstDay = moment().startOf('month')
      if (key === 'value') {
        startDate = firstDay.utc().toISOString()
        endDate = moment().utc().toISOString()
      }
      if (key === 'label') {
        result = `${moment(firstDay).format('L')} - ${moment().format('L')}`
      }
      break
    }

    case 'LAST_SEVEN_DAYS': {
      const firstDay = moment().subtract(7, 'day')
      const yesterday = moment().subtract(1, 'day')
      if (key === 'value') {
        startDate = firstDay.startOf('day').utc().toISOString()
        endDate = yesterday.endOf('day').utc().toISOString()
      }
      if (key === 'label') {
        result = `${moment(firstDay).format('L')} - ${moment(yesterday).format('L')}`
      }
      break
    }

    case 'LAST_MONTH': {
      const firstDay = moment().subtract(1, 'month').startOf('month')
      const lastDay = moment().subtract(1, 'month').endOf('month')
      if (key === 'value') {
        startDate = firstDay.utc().toISOString()
        endDate = lastDay.utc().toISOString()
      }
      if (key === 'label') {
        result = `${moment(firstDay).format('L')} - ${moment(lastDay).format('L')}`
      }
      break
    }

    case 'CUSTOM': {
      if (key === 'value') {
        const firstDay =
            moment(formik?.values?.customDate.from, formatDate) || moment(reserchFromDate, formatDate) || ''
        const lastDay = moment(formik?.values?.customDate.to, formatDate) || moment(reserchToDate, formatDate) || ''
        const fromTime = formik?.values?.customTime?.from
        const toTime = formik?.values?.customTime?.to
        startDate = joinDateAndTime(firstDay, fromTime) || ''
        endDate = joinDateAndTime(lastDay, toTime) || ''
      }
      break
    }

    default:
      break
    }

    if (key === 'value') {
      formik.setFieldValue('date', { from: startDate, to: endDate })
    }

    return result
  }

  useEffect(() => {
    const datesCompleted = []
    dates.map((date) => {
      datesCompleted.push({ value: calcDateRange('label', date), label: date })
      return ''
    })
    setDatesOptions(datesCompleted)
  }, [])

  useEffect(() => {
    if (formik.values.dateType === 'CUSTOM') calcDateRange('value', 'CUSTOM')
  }, [
    formik.values.customDate.from,
    formik.values.customDate.to,
    formik.values.customTime.from,
    formik.values.customTime.to,
    formik.values.dateType
  ])
  
  return (
    <Toggle>
      <MerchantModal
        bigPopup
        closeLabel={intl.formatMessage(captions.close)}
        topBarTitle={formattedMessage.exportTitle}
        onClose={() => actions.closePopup()}
        primaryButtonLabel={formattedMessage.save}
        primaryAction={() => {
          isOperation
            ? actions.doExportOperations(formik.values)
            : isPbl
              ? actions.doExportPbl(formik.values)
              : actions.doExportOrders(formik.values)
        }}
        primaryButtonDisabled={
          timeErrorDetect(formik.values.customDate, formik.values.customTime).start ||
          (formik.values.dateType === 'CUSTOM' && !formik.values.customDate.from) ||
          !formik.values.customDate.to ||
          !formik.isValid ||
          !formik.values.date.from ||
          !formik.values.date.to
        }
        primaryButtonLoading={isLoading}
        secondaryButtonLabel={formattedMessage.cancel}
        secondaryAction={() => actions.closePopup()}
        secondaryButtonDisabled={isLoading}>
        <div className='export_form_container'>
          <h2 className='subtitle'>{formattedMessage.exportDescription}</h2>
          <p className='timeLabel'>{formattedMessage.dateSection}</p>
          <PortalRadioBox
            // formLabel={ formattedMessage.dateSection }
            options={datesOptions.map((a) => ({
              value: a.label,
              id: a.label,
              label:
                a.label !== 'CUSTOM'
                  ? `${intl.formatMessage(captions[a.label])}  ( ${a.value} )`
                  : intl.formatMessage(captions[a.label])
            }))}
            defaultValue={formik.values.dateType}
            onChange={(e) => {
              formik.setFieldValue('dateType', e)
              calcDateRange('value', e)
            }}
          />

          {formik.values.dateType === 'CUSTOM' && (
            <div className='datePicker_container'>
              <p className='dateLabel'>{formattedMessage.dateRange}</p>
              <DatePicker
                placeholder1={formatDate}
                placeholder2={formatDate}
                isRange
                id1='ExportpickerPeriodOfTime'
                minDate={minDate}
                maxDate={maxDate}
                locale={getLanguageCalendar()}
                onClick1={() => outOfRangeCalendar(moment())}
                onChange1={(dateFrom) => {
                  formik.setFieldValue('customDate', {
                    from: setFromDate(dateFrom) || dateFrom,
                    to: formik?.values?.customDate?.to
                  })
                  outOfRangeCalendar(dateFrom)
                }}
                onChange2={(dateTo) => {
                  formik.setFieldValue('customDate', { from: fromDate, to: dateTo })
                  formik.setFieldValue('customTime', {
                    from: moment().startOf('day'),
                    to: dateTo ? moment().endOf('day') : moment()
                  })
                }}
                value1={reserchFromDate || ''}
                value2={reserchToDate || ''}
                label1={intl.formatMessage(captions.from)}
                label2={intl.formatMessage(captions.to)}
                onBlur1={() => formik.setFieldTouched('customDate.from', true)}
                onBlur2={() => formik.setFieldTouched('customDate.to', true)}
                error1={formik?.touched?.customDate?.from && formik?.errors?.customDate?.from}
                errorMessage1={formik?.errors?.customDate?.from}
                error2={formik?.touched?.customDate?.to && formik?.errors?.customDate?.to}
                errorMessage2={formik?.errors?.customDate?.to}
                ariaLabelCalendarButton={intl.formatMessage(captions.calendar)}
              />

              <p className='custom_timeLabel'>{formattedMessage.timeRange}</p>
              <span className='timePicker_container'>
                <MerchantTimePicker
                  format={is12HourFormat() ? 'hh:mm aa' : 'hh:mm'}
                  label={formattedMessage.from}
                  value={isNaN(formik.values.customTime.from) ? null : new Date(formik.values.customTime.from)}
                  onChange={(newValue) =>
                    formik.setFieldValue('customTime', { ...formik.values.customTime, from: moment(newValue) })
                  }
                  name='customTimefrom'
                  disabled={!formik.values.customDate.from || !formik.values.customDate.to}
                  error={formik?.errors?.customTime?.from || formik?.errors?.customTime?.to ? formattedMessage.timeError : null}>
                    
                </MerchantTimePicker>
                <span className='timePickers-dash'>-</span>
                <MerchantTimePicker
                  format={is12HourFormat() ? 'hh:mm aa' : 'hh:mm'}
                  label={formattedMessage.to}
                  name='customTimeto'
                  value={new Date(formik.values.customTime.to)}
                  onChange={(newValue) =>
                    formik.setFieldValue('customTime', { ...formik.values.customTime, to: moment(newValue) })
                  }
                  disabled={!formik.values.customDate.from || !formik.values.customDate.to}
                ></MerchantTimePicker>
              </span>
            </div>
          )}

          <p className='timeLabel'>{formattedMessage.format}</p>
          <PortalRadioBox
            options={format.map((a) => ({ value: a, id: a, label: intl.formatMessage(captions[a]) }))}
            defaultValue='xlsx'
            row
            onChange={(e) => formik.setFieldValue('format', e)}
          />
        </div>
      </MerchantModal>
    </Toggle>
  )
}

ExportPopUp.propTypes = {
  actions: PropTypes.object,
  isLoading: PropTypes.bool,
  reserchFromDate: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  reserchToDate: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  reserchFromTime: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  reserchToTime: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  isOperation: PropTypes.bool,
  isPbl: PropTypes.bool
}

ExportPopUp.defaultProps = {
  actions: {},
  isLoading: false,
  reserchFromDate: '',
  reserchToDate: '',
  reserchFromTime: '',
  reserchToTime: '',
  isOperation: false,
  isPbl: false
}

export default ExportPopUp
